// The GolfBidder component is a React functional component that displays golf trade-in details using structured sections.
//  It renders a hero banner, trade-in steps, representative examples, grading conditions, and terms & conditions, all
//   dynamically sourced from the golfBidder prop. The component utilizes Tailwind CSS for styling and supports
//   image-based content, background images, and icons. It also includes a call-to-action button for finding a store.
//   The layout is responsive, ensuring a user-friendly experience across different devices.

import React from 'react';
import {
  GolfBidderDataSource,
  GradingConditionContent,
  HowToTradeStep,
  RepresentativesContent,
  TermsAndConditionContent,
} from 'types/next';
import Link from 'components/commercetools-ui/atoms/link';

import { useState } from 'react';

interface GolfBidderProps {
  golfBidder: GolfBidderDataSource;
}

const GolfBidder = ({ golfBidder }: GolfBidderProps) => {
  const heroContent = golfBidder?.heroContent || '';
  const tradeIn = golfBidder?.howToTrade || '';
  const representativeExamples = golfBidder?.representatives || '';
  const gradingConditions = golfBidder?.gradingCondition || '';
  const termsAndCondition = golfBidder?.termsAndCondition || '';
  const findStoreButton = golfBidder?.button || '';
  const [showAll, setShowAll] = useState(false);
  const visibleItems = showAll ? termsAndCondition?.content : termsAndCondition?.content?.slice(0, 4);
  const mobileImage = heroContent?.heroMobileImage?.name || '';
  const desktopImage = heroContent?.heroImage?.name || '';

  return (
    <>
      {/* Hero Section */}

      <div className="m-0 overflow-hidden p-0">
        <div
          className="hero-section max-w-auto relative  flex  h-auto w-full items-center justify-center text-center text-white"
          // style={{
          //   backgroundImage: `url(https://americangolf.a.bigcontent.io/v1/static/${heroContent?.heroImage?.name})`,
          //   backgroundSize: 'cover',
          //   backgroundPosition: 'center',
          // }}
        >
          <picture>
            {/*Mobile Image*/}
            <source
              srcSet={`https://americangolf.a.bigcontent.io/v1/static/${mobileImage}`}
              media="(max-width: 645px)"
            />

            {/*Desktop Image*/}
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${desktopImage}`}
              className="h-full w-full object-cover"
              alt="Golfbidder"
            />
          </picture>

          {/* {heroContent?.heroMobileImage && (
            <source
              src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.heroMobileImage?.name}`}
              media="(max-width: 425px)"
              className="w-full h-full object-cover sm:hidden"
            />
          )
            }
          
             {heroContent?.heroImage && (
            <img
              src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.heroImage?.name}`}
              alt="GolfBidder"
              className="w-full h-full object-contain"
            />
          )} */}

          {(heroContent?.logoImg ||
            heroContent?.mainContent ||
            heroContent?.tradeContent ||
            heroContent?.extraPercentage ||
            heroContent?.purchaseContent ||
            heroContent?.tradeInValue ||
            heroContent?.infoContent) && (
            <div className="relative z-10 h-full  max-h-[500px] w-full">
              {heroContent?.logoImg && (
                <img
                  src={`https://americangolf.a.bigcontent.io/v1/static/${heroContent?.logoImg?.name}`}
                  alt="GolfBidder"
                  className="mx-auto w-full max-w-[180px] sm:max-w-[200px] md:max-w-[220px] lg:max-w-[250px]"
                />
              )}
              {heroContent?.mainContent && (
                <h1 className="mb-4 text-lg font-bold sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl">
                  {heroContent?.mainContent}
                </h1>
              )}
              {heroContent?.tradeContent && (
                <p className="mb-4 text-sm font-medium sm:text-base md:text-lg lg:text-xl">
                  {heroContent?.tradeContent}
                </p>
              )}
              {heroContent?.extraPercentage && (
                <h2 className="mb-4 text-lg font-semibold sm:text-xl md:text-2xl lg:text-3xl">
                  {heroContent?.extraPercentage}
                </h2>
              )}
              {heroContent?.purchaseContent && (
                <p className="mb-4 text-base font-medium sm:text-lg md:text-xl lg:text-2xl">
                  {heroContent?.purchaseContent}
                </p>
              )}
              {heroContent?.tradeInValue && (
                <p className="mb-4 text-base font-medium sm:text-lg md:text-xl lg:text-2xl">
                  {heroContent?.tradeInValue}
                </p>
              )}
              {heroContent?.infoContent && (
                <p className="text-base font-medium sm:text-lg md:text-xl lg:text-2xl">{heroContent?.infoContent}</p>
              )}
            </div>
          )}
          {/* <div className="absolute inset-0 bg-black opacity-50 w-full h-full max-h-[500px] "></div> */}
        </div>

        {/* How to Trade In Section */}

        <div className="How-TO-Trade-Section flex w-full max-w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4 py-10 sm:px-6  md:px-10 lg:mx-auto lg:px-0">
          <h2 className=" p-15 text-center font-body text-[calc(1.35rem+1.15vw)] font-semibold tracking-normal lg:py-19">
            {'How to Trade In'}
          </h2>

          <div className="container mx-auto px-4 sm:px-6 lg:px-100 ">
            <div className="grid grid-cols-2 items-stretch justify-center gap-6 sm:grid-cols-4 sm:gap-10 sm:px-10 md:grid-cols-4 md:gap-12 md:px-12 lg:grid-cols-4 lg:gap-16 ">
              {tradeIn?.content &&
                tradeIn?.content.map((item: HowToTradeStep, index: number) => (
                  <div
                    key={index}
                    className="group flex flex-col items-center space-y-4 p-4 text-center sm:p-6 md:p-8 lg:p-10"
                  >
                    <div className="relative flex items-center justify-center" tabIndex={0}>
                      <div className="absolute inset-0 scale-0 rounded-full border-4 border-sky-500 transition-transform duration-300 group-hover:scale-90"></div>
                      <div className="relative m-[15px] flex h-[100px] w-[100px] items-center justify-center  [@media(min-width:425px)]:h-[120px] [@media(min-width:425px)]:w-[120px] [@media(min-width:480px)]:h-[100px] [@media(min-width:480px)]:w-[100px] ">
                        <img
                          src={`https://americangolf.a.bigcontent.io/v1/static/${item?.img?.name}`}
                          className="h-full w-full object-contain  transition-transform duration-300 group-hover:scale-90"
                        />
                      </div>
                    </div>
                    <h3 className="py-4 font-body text-[calc(1.5rem+1.15vw)] font-semibold uppercase md:text-[calc(0.95rem+1.15vw)]">
                      {item.title}
                    </h3>

                    <p className=" justify-stretch px-15 py-4 text-center font-body text-[calc(0.8rem+0.9vw)] font-normal leading-5 md:text-[calc(0.4rem+0.9vw)] md:leading-6 xl:text-[calc(0.3rem+0.9vw)] xl:leading-7">
                      {item.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Representative Examples Section */}

        <div className="Representative-section flex w-full max-w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4 py-10 sm:px-6  md:px-10 lg:mx-auto lg:px-0">
          <p className="p-15 text-center font-body text-[calc(1.35rem+1.15vw)] font-semibold capitalize">
            {representativeExamples?.overallHeading}
          </p>
          <div className="grid grid-cols-1 gap-8 p-10 sm:grid-cols-2 md:grid-cols-3 md:gap-14 md:px-35 lg:grid-cols-3 lg:px-110 2xl:gap-7 2xl:px-150">
            {representativeExamples?.content &&
              representativeExamples?.content.map((item: RepresentativesContent, index: number) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center gap-y-100 overflow-hidden bg-[#00abe4] bg-auto bg-left-top bg-repeat p-4 sm:bg-cover sm:p-6 md:bg-auto md:p-6 lg:bg-auto 2xl:m-4"
                  // style={{
                  //   backgroundImage: `url('https://americangolf.a.bigcontent.io/v1/static/ag-background-logo')`,
                  // }}
                >
                  <div className="group relative block w-full overflow-hidden">
                    <img
                      src={`https://americangolf.a.bigcontent.io/v1/static/${item?.img?.name}`}
                      className="w-full transition-transform duration-500 ease-in-out group-hover:scale-105"
                    />

                    <div className=" absolute bottom-[23%] left-[10px] w-[26%] lg:bottom-[21%] xl:bottom-[18%] ">
                      <img
                        src={`https://${item?.brandImage?.endpoint}.a.bigcontent.io/v1/static/${item?.brandImage?.name}`}
                        alt={item?.brandImage?.name}
                        className="w-full origin-left transition-transform duration-500 ease-in-out group-hover:scale-105"
                      />
                    </div>
                  </div>

                  <span className="[@media(min-width:425px) and max-width:743px]:text-[calc(1.35rem+0.7vw)] absolute right-[15px] top-[15px]  flex h-[20%] max-h-full w-[20%] items-center justify-center rounded-full bg-[#fc5f1a] p-[20px] text-center font-['Source_Sans_Pro'] text-[calc(1.2rem+0.7vw)] font-bold uppercase leading-[19px] text-white  md:text-[calc(0.35rem+1.15vw)]  xl:text-[calc(0.55rem+1.15vw)]">
                    {item?.price}
                  </span>

                  <div className="absolute bottom-0 grid w-full grid-cols-12 ">
                    <h2 className="col-span-8 bg-[#00a2e4] py-[15px] pl-[10px] font-body text-[calc(0.75rem+0.9vw)] font-semibold text-white md:text-[calc(0.15rem+0.9vw)] xl:text-[calc(0.2rem+0.9vw)] ">
                      {item?.heading}
                    </h2>
                    <div className="col-span-4 flex items-center justify-center bg-[#fc5f1a] px-[8px] py-[8px] text-center font-body text-[calc(0.5rem+0.7vw)] font-bold uppercase text-white md:text-[calc(0.05rem+0.7vw)] xl:text-[calc(0.1rem+0.7vw)]">
                      {item?.conditionLabel}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Grading Conditions Section */}

        <div className="Grading-Section  flex w-full max-w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4 py-10 sm:px-6  md:px-10 lg:mx-auto lg:px-0">
          <p className="p-15 text-center font-body text-[calc(1.35rem+1.15vw)] font-semibold capitalize">
            {gradingConditions?.overallHeading}
          </p>
          <div className=" mx-auto px-4 py-8 sm:px-6 md:px-20 lg:px-20 2xl:px-28">
            <div className="grid w-full grid-cols-2 justify-items-center gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-6 md:gap-10 md:px-20 lg:grid-cols-6 lg:px-100 xl:px-120">
              {gradingConditions?.content &&
                gradingConditions.content.map((item: GradingConditionContent, index: number) => (
                  <div key={index} className="flex w-full flex-col items-center space-y-2 px-2  text-center sm:px-6">
                    <div className="group relative flex items-center justify-center">
                      <div className="absolute inset-0 scale-0 rounded-full border-4 border-sky-500 transition-transform duration-300 group-hover:scale-100"></div>

                      <div className="relative z-10 flex h-[105px] w-[105px] items-center justify-center">
                        <img
                          src={`https://americangolf.a.bigcontent.io/v1/static/${item?.img?.name}`}
                          alt={item?.description}
                          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-90"
                        />
                      </div>
                    </div>

                    <p
                      className="text-center font-body text-[calc(0.8rem+0.7vw)] font-normal md:text-[calc(0.3rem+0.9vw)]"
                      style={{ padding: '17px' }}
                    >
                      {item?.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Terms & Conditions Section */}

        <div className="Terms-Condition-Section  flex w-full max-w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4 py-10 sm:px-6  md:px-10 lg:mx-auto lg:px-0">
          <p className="p-15 text-center font-body text-[calc(1.35rem+1.15vw)] font-semibold capitalize">
            {'Terms & Condition'}
          </p>
          <ul className="w-full max-w-[900px] list-disc space-y-4 p-4 pl-35 pr-10 text-left sm:space-y-3 sm:p-5 md:pl-55 xl:pl-45 ">
            {visibleItems?.map((item: TermsAndConditionContent, index: number) => (
              <li
                key={index}
                className=" list-disc break-words pl-[15px]  text-[16px] font-normal leading-relaxed text-gray-900 "
              >
                {item?.paragraphText}
              </li>
            ))}
          </ul>

          {termsAndCondition?.content && termsAndCondition?.content?.length > 4 && (
            <button onClick={() => setShowAll(!showAll)} className="mt-4 flex items-center text-black">
              <span className="text-base">{showAll ? 'Read Less' : 'Read More'}</span>
              <span className="ml-6 text-2xl font-semibold sm:text-3xl ">{showAll ? '-' : '+'}</span>
            </button>
          )}
        </div>

        {/* Find Store Section */}

        <div className="Find-store-section mb-28 flex w-full max-w-full flex-col items-center justify-center gap-y-10 overflow-hidden px-4 py-10 sm:px-6  md:px-10 lg:mx-auto lg:px-0">
          <Link link={findStoreButton?.link}>
            <button
              className="group relative h-[60px] w-[300px] max-w-[800px] animate-[heartbeat_3s_ease-in-out_infinite] overflow-hidden rounded-[80px] px-5 transition-all 
      duration-500 ease-in-out  hover:shadow-xl sm:h-[60px] sm:max-w-[500px] md:w-[420px] md:max-w-[600px] lg:w-[700px]"
            >
              <span className="absolute inset-0 rounded-[80px] bg-[#00abe4]"></span>

              <span
                className="absolute inset-0 translate-x-[-100%] rounded-[80px] bg-[#008bb5] 
  transition-transform duration-500 ease-in-out group-hover:translate-x-0"
              />
              <span
                className="absolute inset-0 rounded-[80px] bg-[#008bb5] opacity-0 
  transition-opacity duration-500 ease-in-out group-hover:opacity-100 
  group-hover:delay-500"
              />

              <span className="relative z-10 text-[18px] font-semibold uppercase text-white sm:text-[20px] md:text-[22px]">
                {findStoreButton?.name}
              </span>
            </button>
          </Link>
        </div>
      </div>
    </>
  );
};

export default GolfBidder;
